import { DOCUMENT } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "servicepro-theme";
const DARK_CLASS = "dark-theme";
const TRANSITION_CLASS = "theme-transition";
const THEME_TRANSITION_MS = 1200;

@Injectable({ providedIn: "root" })
export class ThemeService {
    private readonly document = inject(DOCUMENT);
    public readonly activeTheme = signal<ThemeMode>(this.resolveInitialTheme());
    private themeTransitionTimeoutId: number | null = null;

    constructor() {
        this.resetTransitionState();
        this.applyTheme(this.activeTheme(), false);
    }

    public toggleTheme(): void {
        const next: ThemeMode = this.activeTheme() === "light" ? "dark" : "light";
        this.setTheme(next, true);
    }

    public setTheme(mode: ThemeMode, withTransition: boolean = false): void {
        this.activeTheme.set(mode);
        this.applyTheme(mode, withTransition);
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // localStorage unavailable (e.g. incognito with storage disabled)
        }
    }

    private applyTheme(mode: ThemeMode, withTransition: boolean): void {
        const body = this.document.body;
        if (withTransition) {
            this.applyTemporaryTransitionClass(body);
        } else {
            this.resetTransitionState();
        }

        if (mode === "dark") {
            body.classList.add(DARK_CLASS);
        } else {
            body.classList.remove(DARK_CLASS);
        }
    }

    private applyTemporaryTransitionClass(body: HTMLElement): void {
        const win = this.document.defaultView;
        if (!win) return;

        body.classList.add(TRANSITION_CLASS);

        if (this.themeTransitionTimeoutId !== null) {
            win.clearTimeout(this.themeTransitionTimeoutId);
        }

        this.themeTransitionTimeoutId = win.setTimeout(() => {
            body.classList.remove(TRANSITION_CLASS);
            this.themeTransitionTimeoutId = null;
        }, THEME_TRANSITION_MS);
    }

    private resetTransitionState(): void {
        const body = this.document.body;
        body.classList.remove(TRANSITION_CLASS);

        const win = this.document.defaultView;
        if (!win || this.themeTransitionTimeoutId === null) return;

        win.clearTimeout(this.themeTransitionTimeoutId);
        this.themeTransitionTimeoutId = null;
    }

    private resolveInitialTheme(): ThemeMode {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored === "dark" || stored === "light") {
                return stored;
            }
        } catch {
            // ignore
        }
        // Fall back to OS preference
        if (this.document.defaultView?.matchMedia?.("(prefers-color-scheme: dark)")?.matches) {
            return "dark";
        }
        return "light";
    }
}
